import { UserPlus, Mail } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useState } from "react";
import { toast } from "sonner";
import { useMutation } from "convex/react";
import { api } from "@/convex";

const Refer = () => {
  const createReferral = useMutation(api.referrals.create);
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    peerName: "",
    peerEmail: "",
    peerCompany: "",
    peerTitle: "",
    peerPhone: "",
    message: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createReferral({
        peerName: formData.peerName,
        peerEmail: formData.peerEmail,
        peerCompany: formData.peerCompany,
        peerTitle: formData.peerTitle,
        peerPhone: formData.peerPhone || undefined,
        message: formData.message || undefined,
      });
      toast.success("Referral submitted successfully! We'll reach out to your peer soon.");
      setFormData({
        peerName: "",
        peerEmail: "",
        peerCompany: "",
        peerTitle: "",
        peerPhone: "",
        message: ""
      });
    } catch {
      toast.error("Failed to submit referral. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-6 py-10 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-4xl font-semibold text-foreground mb-2 tracking-tight">
            Refer a Peer
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base tracking-wide">
            Help us grow the Energy Talking Point Executive Network.
          </p>
        </div>

        <div className="max-w-3xl space-y-6">
          {/* Referral Form */}
          <Card>
            <CardHeader>
              <CardTitle>Referral Form</CardTitle>
              <CardDescription>Share information about an executive who would benefit from our network</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Peer Information</h3>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="peerName">Full Name *</Label>
                      <Input
                        id="peerName"
                        name="peerName"
                        value={formData.peerName}
                        onChange={handleChange}
                        placeholder="Jane Smith"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="peerEmail">Email *</Label>
                        <Input
                          id="peerEmail"
                          name="peerEmail"
                          type="email"
                          value={formData.peerEmail}
                          onChange={handleChange}
                          placeholder="jane.smith@example.com"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="peerPhone">Phone Number</Label>
                        <Input
                          id="peerPhone"
                          name="peerPhone"
                          type="tel"
                          value={formData.peerPhone}
                          onChange={handleChange}
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="peerCompany">Company *</Label>
                        <Input
                          id="peerCompany"
                          name="peerCompany"
                          value={formData.peerCompany}
                          onChange={handleChange}
                          placeholder="Energy Corp International"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="peerTitle">Title *</Label>
                        <Input
                          id="peerTitle"
                          name="peerTitle"
                          value={formData.peerTitle}
                          onChange={handleChange}
                          placeholder="Chief Operating Officer"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Why would they be a good fit?</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Share any relevant details about their experience, expertise, or why they'd benefit from our network..."
                        rows={4}
                      />
                    </div>
                  </div>
                </div>

                <Button type="submit" size="lg" className="w-full" disabled={submitting}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  {submitting ? "Submitting..." : "Submit Referral"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Referral Criteria */}
          <Card>
            <CardHeader>
              <CardTitle>Who to Refer</CardTitle>
              <CardDescription>We still have capacity, so now is a great time to refer potential ETP members.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm font-medium text-foreground">My 4 criteria are:</p>
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                <li>Alignment on energy abundance and energy freedom</li>
                <li>Deep expertise in something useful for energy advocacy</li>
                <li>Independent thinking</li>
                <li>Fun to be around</li>
              </ol>
            </CardContent>
          </Card>

          {/* Reach Out Directly */}
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground mb-4">
                Prefer to make the introduction yourself? Use our email template to personally refer a peer.
              </p>
              <Button
                onClick={() => setIsTemplateOpen(true)}
                size="lg"
                className="w-full"
              >
                <Mail className="w-4 h-4 mr-2" />
                View Email Template
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Side Drawer for Email Template */}
        <Sheet open={isTemplateOpen} onOpenChange={setIsTemplateOpen}>
          <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Referral Email Template</SheetTitle>
              <SheetDescription>Use this template to introduce a peer to ETP</SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-4">
              <div>
                <p className="font-semibold mb-1 text-sm">Subject:</p>
                <p className="text-muted-foreground">Energy Talking Points</p>
              </div>
              <div>
                <p className="font-semibold mb-1 text-sm">CC:</p>
                <p className="text-muted-foreground"><a href="mailto:alex@alexepstein.com" className="text-primary hover:underline">alex@alexepstein.com</a>, <a href="mailto:michelle@alexepstein.com" className="text-primary hover:underline">michelle@alexepstein.com</a></p>
              </div>
              <div className="border-t pt-4">
                <div className="text-foreground leading-relaxed space-y-4">
                  <p>Hi [NAME],</p>

                  <p>I wanted to introduce you to Alex Epstein, energy expert and the author of <em>Fossil Future</em>. Alex is leading an effort called Energy Talking Points, which is shaping the country's energy policy for the better.</p>

                  <p>Energy Talking Points (ETP) gives politicians the exact policies, messaging, and personnel recommendations they need to promote energy freedom. Over the last year, ETP's influence has exploded. Alex and his team have been credited by the Trump transition team for helping shape key energy appointments. During the "Big Beautiful Bill" negotiations, the policy ideas circulated by the ETP team led to deeper cuts in solar/wind subsidies—covered by outlets like <em>The New York Times</em>, <em>WSJ</em>, and <em>Politico</em>.</p>

                  <p>The crucial work of ETP is supported by a community of over 130 members, including myself. I've found membership rewarding. There are regular member calls, updates with policy leaders, and a fantastic annual conference. This year's lineup included DOE Secretary Chris Wright, Deputy DOE Secretary James Danly, Deputy EPA Administrator David Fotouhi, Senator Mike Lee, Mike Rowe, and more.</p>

                  <p>Membership is $25K per year. Would you consider joining?</p>

                  <p>[Your name]</p>

                  <p>PS I've copied Alex and his chief of staff Michelle on this email in case you have any questions for them.</p>
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
};

export default Refer;
